<script setup lang="ts">
    import { useDisplay, useTheme } from 'vuetify';
    import { Bookmaker, Currency, EventFeedType, UserType, EventName, BetOrderStatus, BookmakerCode } from '@/constants';
    import utils from '@/helpers/utils';
    import BetDialogConfirm from '@/pages/core/betDialog/BetDialogConfirm.vue';
    import BetDialogConfirmDuplicate from '@/pages/core/betDialog/BetDialogConfirmDuplicate.vue';
    import BetDialogEvent from '@/pages/core/betDialog/BetDialogEvent.vue';
    import BetDialogInfo from '@/pages/core/betDialog/BetDialogInfo.vue';
    import BetDialogBetInfo from '@/pages/core/betDialog/BetDialogBetInfo.vue';
    import eventBus from '@/services/eventBus';
    import indexOf from 'lodash-es/indexOf';
    import sortBy from 'lodash-es/sortBy';
    import { useAppStore } from '@/stores/app';
    import { useUserStore } from '@/stores/user';
    import { EventPeriod } from '@/constants';
    import { betOrderLiveTimeoutDictionary, betOrderPregameTimeoutDictionary, useBookmakerDictionary } from '@/stores';
    import { formatOdds, getCountryFlagUrl } from '@/helpers';
    import { BettingData } from '@/models/bettingData';
    import { OrderFactory } from '@/models/orderFactory';
    import { IOrderAppApi } from '@/types';
    import { onBetPopupPointerDown } from '@/pages/core/betDialog/bettingDialogInteraction';
    import { useRoute } from 'vue-router';
    import { OrderItem } from '@/models/order';
    import { AppError } from '@/errors/appError';
    import { usePlatform } from '@/composables/usePlatform';
    import { useNumberFormatter } from '@/composables/useNumberFormatter';

    const {
        id = '0',
        width = 450,
        contentClass = '',
        idPrefix = 'bet-dialog-',
    } = defineProps<{
        id: string;
        width?: number;
        contentClass?: string;
        idPrefix?: string;
    }>();

    const emit = defineEmits(['cancel']);
    const { t, locale } = useI18n();
    const { formatNumber } = useNumberFormatter();
    const theme = useTheme();
    const display = useDisplay();
    const { xs } = useDisplay();
    const bookmakerDictionary = useBookmakerDictionary();
    const platform = usePlatform();

    const dialogOpen = ref(false);
    // const zIndex = ref(undefined);
    const loading = ref(false);
    const disabled = ref(true);

    const defaultSuggestedPrices = reactive({
        [Currency.Euro]: [2, 5, 10, 20, 50, 100],
        [Currency.Gbp]: [2, 5, 10, 20, 50, 100],
        [Currency.Brl]: [6, 10, 20, 50, 100, 150],
        [Currency.Usd]: [2, 5, 10, 20, 50, 100],
    });

    const sites = ref<number[]>([]);
    const errorMessage = ref('');
    const warningMessage = ref('');
    const isOrderConfirmed = ref(false);
    const appStore = useAppStore();
    const userStore = useUserStore();

    const dialogRef = ref<any>();
    provide('dialogRef', dialogRef);
    const changeOrderSettingsDialog = inject<Ref>('changeOrderSettingsDialogRef');
    const betConfirmRef = ref<any>(null);
    const betConfirmDuplicateRef = ref<any>(null);
    const formBettingRef = ref<any>(null);

    const showChangeOrderSettingsDialog = () => {
        if (changeOrderSettingsDialog && changeOrderSettingsDialog.value) {
            changeOrderSettingsDialog.value.open();
        }
    };

    const rules = {
        timeout() {
            // if (!isNumber(bettingData.value.timeout)) {
            //     return t('rules.timeoutRequired');
            // }
            return true;
        },
        stake(v: number) {
            if (utils.isEmpty(v) || v == 0) {
                return t('rules.stakeRequired');
            } else if (!utils.isNumeric(v)) {
                return t('rules.integer');
            } else if (v < 0) {
                return t('rules.negative');
            } else {
                let minStakes = appStore.betOrderMinStakes;
                let allowedSites = userStore.settings.bet_order_site_ids_allowed;
                let minArr: number[] = [];

                if (platform.isAggregator()) {
                    minStakes = minStakes.filter((minStake: any) => allowedSites.indexOf(minStake[0]) !== -1);
                }

                minStakes.forEach(function (val: any) {
                    //PINNA changed it, no need to re-calculate minStake
                    // if (userStore.currency.id === 4 && val[0] === 10) {
                    //     let pinnaOdds = bettingData.value.priceList ? bettingData.value.priceList.filter((s) => s[0] === val[0]) : [];
                    //
                    //     if (pinnaOdds[0] && pinnaOdds[0][1] < 1.9) {
                    //         minArr.push(Math.floor((5.4 / (pinnaOdds[0][1] - 1) / 70) * 100));
                    //         return;
                    //     }
                    // }
                    minArr.push(val[1]);
                });

                let minValue = Math.min(...minArr);

                if (v < minValue) {
                    return t('rules.minStake', {
                        currency: userStore.currency.symbol,
                        value: minValue,
                    });
                }

                let maxValue = userStore.balance.credit;

                if (v > maxValue) {
                    return t('rules.checkBalance');
                }

                return true;
            }
        },
        odds(v: string) {
            if (utils.isEmpty(v)) {
                return t('rules.oddsRequired');
            } else if (parseFloat(v) < 1.1) {
                return t('rules.min', { value: 1.1 });
            }
            return true;
        },
    };

    const bettingData = computed<BettingData>(() => appStore.betFeedData[id]);

    const getTimeoutDictionary = computed(() => (bettingData.value.eventFeedType === EventFeedType.Live ? betOrderLiveTimeoutDictionary.items : betOrderPregameTimeoutDictionary.items));

    const suggestedPrices = computed(() =>
        userStore.settings.bet_order_suggested_prices_default ? userStore.settings.bet_order_suggested_prices_default : defaultSuggestedPrices[userStore.currency.id]
    );

    const siteOrdered = computed(() => {
        let orderArr = [Bookmaker.Pinna, Bookmaker.Betfair, Bookmaker.Matchbook, Bookmaker.Betdaq, Bookmaker.Isn, Bookmaker.ThreeBet, Bookmaker.Sharpbet, Bookmaker.Ibc, Bookmaker.Sbo];
        let arr: Bookmaker[] = sortBy(userStore.settings.bet_order_site_ids_allowed, function (id) {
            return indexOf(orderArr, id);
        });

        const isLive = bettingData.value.eventFeedType === EventFeedType.Live;

        const items = isLive
            ? bookmakerDictionary.items.filter((bookmaker) => bookmaker.inLiveFeed && bookmaker.isVisibleForPlayer && bookmaker.isActive)
            : bookmakerDictionary.items.filter((bookmaker) => bookmaker.inPregameFeed && bookmaker.isVisibleForPlayer && bookmaker.isActive);
        return arr.filter((bookmakerId) => items.find((item) => item.id === bookmakerId) !== undefined);
    });

    const getId = computed(() => idPrefix + id);

    const stakeButtonStyle = computed(() => {
        let width = '55px';
        if (siteOrdered.value.length > 5) {
            width = 100 / siteOrdered.value.length + '%';
        }
        return 'width: ' + width;
    });

    const route = useRoute();

    const prices = computed(() => {
        if (!bettingData.value.isActive) {
            return [];
        }
        //skip reactivity
        let prices = JSON.parse(JSON.stringify(bettingData.value.priceList));

        //add extra odds
        if (bettingData.value.extraOdds !== undefined) {
            Object.keys(bettingData.value.extraOdds).forEach((idx) => {
                const extraOdds = bettingData.value.extraOdds![Number(idx) as Bookmaker];
                Object.keys(extraOdds).forEach((odds) => {
                    prices.push([parseInt(idx), parseFloat(odds), extraOdds[Number(odds)]]);
                });
            });

            prices = prices.sort((a: [number, number], b: [number, number]) => (a[1] < b[1] ? 1 : b[1] < a[1] ? -1 : 0));
        }

        //skip prices with min stake lower than current stake
        prices = prices.filter((price: [number, number]) => {
            return !utils.isNumeric(bettingData.value.stake) || Number(idxMinStake(price[0])) <= Number(bettingData.value.stake);
        });

        //Add max stake values
        prices.forEach((price: [number, number], idx: number) => {
            if (!prices[idx][2] && bettingData.value.oddsMaxStake && bettingData.value.oddsMaxStake[price[0] as Bookmaker]) {
                prices[idx][2] = bettingData.value.oddsMaxStake[price[0] as Bookmaker];
            }

            prices[idx][1] = formatOdds(prices[idx][1]);
        });

        let groupedPrices: any = [];
        let groupedPricesIdx = 0;
        //group prices with same odds
        prices.forEach((price: [number, number, number], idx: number) => {
            if (idx === 0 || formatOdds(price[1]) !== formatOdds(prices[idx - 1][1])) {
                groupedPrices.push(price);
                groupedPricesIdx++;
            } else {
                if (groupedPrices[groupedPricesIdx - 1] && groupedPrices[groupedPricesIdx - 1][2] && price[2]) {
                    groupedPrices[groupedPricesIdx - 1][2] += price[2];
                }
            }
        });

        prices = groupedPrices;

        //filter prices without liquidity
        prices = prices.filter((price: [number, number, number], idx: number) => {
            return price[2] !== undefined;
        });

        //liquidity
        prices.forEach((price: [number, number, number], idx: number) => {
            if (idx !== 0) {
                price[2] += prices[idx - 1][2];
            }
        });

        let priceCount = prices.length;
        if (priceCount >= 6) {
            return prices.slice(0, 6);
        }
        return prices.concat(Array(6 - priceCount));
    });

    function idxMinStake(idx: number): number | null {
        let minStake = appStore.betOrderMinStakes.filter((minStake) => minStake[0] === idx);

        // if (this.user.currency === this.$const.CURRENCY_BRL_ID && idx === 10) {
        //     if (this.odds < 1.9) {
        //         minStake = Math.floor((5.4 / (this.odds - 1) / 70) * 100);
        //     }
        // }

        return minStake ? minStake[0][1] : null;
    }

    watch(
        () => route.name,
        () => {
            //we need to close betting popup if route changed (or user is logged out)
            if (dialogOpen.value) cancel();
        }
    );

    watch(
        () => bettingData.value?.eventFeedType,
        (val: EventFeedType, prevVal: EventFeedType) => {
            if (prevVal && val === EventFeedType.Live) {
                bettingData.value.timeout = userStore.settings.bet_order_live_timeout_default;
            }
        }
    );

    function duplicateOrders() {
        const isLive = bettingData.value.eventFeedType === EventFeedType.Live;

        return appStore.orders.filter(
            (order) =>
                order.customEventId === bettingData.value.customEventId &&
                order.statusId !== BetOrderStatus.Closed &&
                order.betSelectionId === bettingData.value.betSelectionId &&
                Number(order.isLive) === Number(isLive)
        );
    }

    function sitesValidation(v: any) {
        let validRequired: string | boolean = t('rules.sitesRequired');
        let validOdds = false;
        if (Array.isArray(v)) {
            v.forEach(function (siteId) {
                if (bettingData.value.siteOdds[siteId] && bettingData.value.siteMinStake(siteId) <= Number(bettingData.value.stake)) {
                    validRequired = true;
                    validOdds = true;
                } else if (bettingData.value.siteOdds[siteId]) {
                    validOdds = true;
                }
            });
        } else if (bettingData.value.siteOdds[v]) {
            validRequired = true;
            validOdds = true;
        }
        return validRequired !== true ? (validOdds ? t('rules.sitesOdds') : validRequired) : validRequired;
    }

    function normalizePrice(price: string) {
        if (siteOrdered.value.length >= 7 && price.length >= 6) {
            price = formatOdds(price, 2);
        }
        return price;
    }

    function open(correctPosition = true) {
        dialogOpen.value = true;

        isOrderConfirmed.value = false;

        if (bettingData.value.orderPlaced) {
            bettingData.value.orderPlaced = null;

            bettingData.value.initTimeout();
            bettingData.value.initOdds();
            bettingData.value.initStake();
        }

        if (siteOrdered.value.length > 0) {
            sites.value = [...siteOrdered.value];
        }

        change();

        if (correctPosition) {
            nextTick(() => {
                let length = Object.keys(appStore.betFeedData).length;
                if (length > 0) {
                    let shift = length * 40;
                    const element = document.getElementById(getId.value);
                    if (element && element.parentNode instanceof HTMLElement) {
                        const el = element.parentNode;
                        const firstChild = el.children[0];
                        if (firstChild) {
                            let left = window.innerWidth / 2 - firstChild.getBoundingClientRect().width + shift + 160;
                            let top = window.innerHeight / 2 - firstChild.getBoundingClientRect().height + shift + 180;
                            el.style.setProperty('position', 'fixed');
                            el.style.setProperty('margin', '0px');
                            el.style.setProperty('left', left + 'px');
                            el.style.setProperty('top', top + 'px');
                        }
                    }
                }
            });
        }
    }

    function close() {
        dialogOpen.value = false;
        if (formBettingRef.value && formBettingRef.value.reset) formBettingRef.value.reset();
        eventBus.off(EventName.OrderChanged);
    }

    function cancel() {
        close();
        let betFeedItem = bettingData;

        delete appStore.betFeedData[id];
        emit('cancel', {
            betFeedData: betFeedItem,
            id: id,
        });
    }

    defineExpose({ open, cancel, isCreatedFromFeed: computed(() => bettingData.value?.isCreatedFromFeed) });

    function newOrder() {
        open(false);
        eventBus.off(EventName.OrderChanged);

        bettingData.value.orderPlaced = null;
    }

    function acceptOrder(isOrderDuplicateConfirmed: boolean = false) {
        if (betConfirmRef.value) betConfirmRef.value.close();

        if (!isOrderDuplicateConfirmed) {
            if (userStore.settings.bet_order_is_request_bet_duplicate_confirmation_default && duplicateOrders().length > 0) {
                betConfirmDuplicateRef.value.open();
                return false;
            }
        }

        betConfirmDuplicateRef.value.close();
        isOrderConfirmed.value = true;
        placeOrder(true);
    }

    function setRecommendedStake(price: number) {
        bettingData.value.stake = price;
        change();
    }

    function setOdds(oddsVal: string) {
        if (oddsVal && bettingData.value.orderPlaced === null) {
            bettingData.value.odds = oddsVal;
            change();
        }
    }

    function focus() {
        // see original for full implementation
    }

    function change() {
        if (userStore.type !== UserType.Player) {
            disabled.value = true;
            return;
        }
        nextTick(async () => {
            if (formBettingRef.value) {
                let siteOrd = siteOrdered.value.filter((site) => bettingData.value.siteMinStake(site) <= Number(bettingData.value.stake));
                if (platform.isAggregator() && siteOrd.length === 0) {
                    disabled.value = true;
                    await formBettingRef.value.validate();
                    getValidationError();
                } else {
                    const validateResult = await formBettingRef.value.validate();
                    if (validateResult?.valid) {
                        disabled.value = false;
                        errorMessage.value = '';
                    } else {
                        disabled.value = true;
                        getValidationError();
                    }
                }
            }
        });
    }

    function minStakeWarning() {
        if (!bettingData.value.isActive) {
            return false;
        }
        let prices = bettingData.value.priceList;
        if (prices && prices[0] && bettingData.value.siteMinStake(prices[0][0]) > Number(bettingData.value.stake) && isOrderConfirmed.value === false) {
            warningMessage.value = t('betDialog.minOddsWarning', {
                odds: formatOdds(prices[0][1], 3),
                currency: userStore.currency.symbol,
                stake: bettingData.value.siteMinStake(prices[0][0]),
            });
        } else {
            warningMessage.value = '';
        }
        return true;
    }

    function getValidationError() {
        if (formBettingRef.value && formBettingRef.value.errors && formBettingRef.value.errors.length) {
            errorMessage.value = formBettingRef.value.errors[0].errorMessages[0];
        }
    }

    async function confirmAction() {
        if (userStore.type !== UserType.Player) {
            disabled.value = true;
            return;
        }
        const validateResult = await formBettingRef.value.validate();
        if (validateResult?.valid) {
            if (userStore.settings.bet_order_is_request_bet_confirmation_default) {
                betConfirmRef?.value.open();
            } else if (userStore.settings.bet_order_is_request_bet_duplicate_confirmation_default && duplicateOrders().length > 0) {
                betConfirmDuplicateRef?.value.open();
            } else {
                isOrderConfirmed.value = true;
                placeOrder();
            }
        } else {
            disabled.value = true;
            errorMessage.value = validateResult.errors[0].errorMessages[0];
        }
    }

    async function placeOrder(isNoScoreAndTimeAccepted = false) {
        loading.value = true;

        // eventBus.on(EventName.OrderChanged, (data: Partial<OrderItem>) => {
        //     if (data.id === bettingData.value.orderPlaced!.id) {
        //         bettingData.value.orderPlaced = Object.assign(bettingData.value.orderPlaced!, data);
        //     }
        // });

        let response;

        try {
            response = await bettingData.value.placeOrder(sites, isNoScoreAndTimeAccepted);
        } finally {
            loading.value = false;
        }

        if (response.data.success) {
            errorMessage.value = '';
            formBettingRef.value?.resetValidation();
            if (!userStore.settings.bet_order_is_keep_order_popup_open_default) {
                cancel();
            } else {
                const orderPlaced = OrderFactory.createFromAppApi(response.data.row as IOrderAppApi);
                appStore.addOrder(orderPlaced);

                const order = appStore.orders.find((o) => o.id === orderPlaced.id) as OrderItem | undefined;

                if (!order) throw new AppError(`Order #${orderPlaced.id} not found after placing`);
                bettingData.value.orderPlaced = order as OrderItem;
            }
        } else {
            let message;
            try {
                message = JSON.parse(response.data.message);
                for (let key in message[1]) {
                    message[1][key] = t(message[1][key]) ? t(message[1][key]) : message[1][key];
                }
            } catch (e) {
                message = response.data.message;
            }
            errorMessage.value = Array.isArray(message) ? t(message[0], message[1]) : t(message);
        }
    }

    onMounted(() => {
        console.log('Component BetDialog mounted.');
    });
    onBeforeUnmount(() => {
        console.log('Component BetDialog destroyed.');
    });
</script>
<template>
    <v-dialog
        v-if="dialogOpen"
        v-model="dialogOpen"
        :content-class="contentClass"
        :fullscreen="xs"
        :no-click-animation="true"
        :retain-focus="false"
        :scrim="false"
        :scrollable="xs"
        :width="width"
        persistent
        @keydown.esc="cancel"
        @keydown.enter="cancel"
        @pointerdown="onBetPopupPointerDown"
    >
        <BetDialogConfirm :id="id" ref="betConfirmRef" @confirmed="acceptOrder()"></BetDialogConfirm>

        <BetDialogConfirmDuplicate :id="id" ref="betConfirmDuplicateRef" @confirmed="acceptOrder(true)"></BetDialogConfirmDuplicate>

        <v-card :id="getId" class="bet-dialog" :class="display.width.value < 400 ? 'pa-1' : 'pa-3'" @mousedown="focus">
            <v-card-title class="px-0 pt-0 pb-2" :class="!bettingData.isOrderPlaced ? '' : 'pb-0'">
                <v-container class="pa-3">
                    <v-row class="popup-header popup-header-title font-weight-regular h-27">
                        <span class="popup-header pr-1 text-right popup-header-green">
                            <span v-if="bettingData.periodTypeId === EventPeriod.FullTime">{{ t('betDialog.marketFT') }}</span>
                            <span v-else>{{ t('betDialog.market1H') }}</span>
                            |
                        </span>
                        <span class="popup-header popup-header-green">
                            {{ bettingData.selectionLabel }}
                        </span>

                        <!--                    <v-spacer></v-spacer>-->

                        <span
                            v-if="bettingData.eventFeedType === EventFeedType.Live"
                            class="d-flex pl-2 flex-column align-center justify-center"
                            :class="[bettingData.scoreFull && bettingData.time ? 'pb-2px' : '']"
                        >
                            <v-chip color="orange" label density="default" class="live-chip" variant="outlined">
                                <span v-if="bettingData.scoreFull && bettingData.time">LIVE&nbsp;{{ bettingData.time }}</span>
                                <span v-else class="time bold" :class="locale === 'pt' ? 'pt' : ''">LIVE&nbsp;{{ t('feed.scoreNotConfirmed') }}</span>
                            </v-chip>
                        </span>

                        <v-spacer></v-spacer>

                        <span class="cursorPointer lightGreyText" @click="showChangeOrderSettingsDialog">
                            <v-icon class="mb-2px" :color="theme.name.value === 'dark' ? '' : '#867878'" :icon="mdiCogOutline" />
                        </span>
                    </v-row>
                    <v-row class="popup-header h-26">
                        <span class="">
                            <img v-if="bettingData.iso3" :alt="bettingData.iso3" :src="getCountryFlagUrl(bettingData.iso3)" class="pr-1 mb-2 h-15" />
                            <img v-else alt="world" :src="getCountryFlagUrl('world')" class="pr-1 mb-2 h-15" />
                        </span>

                        <span class="popup-header font14 font-weight-regular">{{ bettingData.leagueName }}</span>
                        <v-spacer></v-spacer>
                    </v-row>
                    <v-row class="popup-header h-26 mt-1">
                        <BetDialogEvent :id="id"></BetDialogEvent>
                        <v-spacer></v-spacer>
                    </v-row>
                    <v-row class="popup-header">
                        <BetDialogInfo :id="id" :odds="bettingData.odds"></BetDialogInfo>
                    </v-row>
                </v-container>
            </v-card-title>

            <v-form ref="formBettingRef" class="h-100" lazy-validation @keyup.enter="confirmAction">
                <v-card class="pa-0 pt-3 pb-3" flat>
                    <v-row v-if="!bettingData.isOrderPlaced && userStore.settings.bet_order_is_suggested_prices_default" class="px-3 pt-1">
                        <v-col v-for="(price, index) in suggestedPrices" :key="index" class="py-1 text-center pl-0 pr-1px">
                            <v-chip :color="theme.name.value === 'dark' ? '#404149' : '#d9ead3'" class="suggested suggested-chip" size="small" variant="flat" @click="setRecommendedStake(price)">
                                {{ userStore.currency.symbol }}{{ formatNumber(price, 'moneyNoCents') }}
                            </v-chip>
                        </v-col>
                    </v-row>
                    <v-row v-if="platform.isBookmaker()" class="px-3 py-1" :class="[bettingData.isOrderPlaced ? 'pb-2' : '']">
                        <v-col
                            v-for="(price, index) in prices"
                            cols="2"
                            class="py-0 px-1 liquidity"
                            :class="[index > 4 ? 'liquidity-last' : '', index === 0 && price && price[index] ? 'max-price' : '', bettingData.isOrderPlaced ? 'cursorPointer' : 'no-hover']"
                            :key="index"
                            @click="price && price[1] ? setOdds(price[1]) : null"
                        >
                            <div v-if="price && price[1]" class="font13 price" :class="index === 0 ? 'bold' : ''">
                                {{ formatOdds(price[1]) }}
                            </div>
                            <div v-else style="height: 16px">
                                <br />
                                <br />
                            </div>
                            <div v-if="price && price[2]" class="max-stake text-center font10 font-weight-regular">{{ userStore.currency.symbol }}{{ formatNumber(price[2], 'moneyNoCents') }}</div>
                            <div v-else-if="price && userStore.type === UserType.Player && price[0]" class="max-stake text-center font-weight-regular">
                                {{ t('betDialog.loading') }}
                            </div>
                            <div v-else class="max-stake">&nbsp;</div>
                        </v-col>
                    </v-row>
                    <v-row v-if="!bettingData.isOrderPlaced" class="px-0">
                        <v-col class="pt-2 pr-0">
                            <v-text-field
                                v-model="bettingData.odds"
                                :label="t('betDialog.odds')"
                                :rules="[rules.odds(bettingData.odds)]"
                                class="font14 bet bet-odds bold"
                                :class="[Number(bettingData.odds) > Number(bettingData.price) ? 'bet-odds-warning' : '']"
                                density="compact"
                                :hide-details="true"
                                min="1"
                                required
                                step="0.05"
                                type="number"
                                variant="outlined"
                                @focus="focus()"
                                @input="change()"
                            >
                                <template v-slot:prepend-inner>
                                    <span class="font12 greyText">@</span>
                                    <!--                                <v-icon color="grey" small class="pt-1" :icon="mdiAt" />-->
                                </template>
                            </v-text-field>
                        </v-col>
                        <v-col class="pt-2 px-1">
                            <v-text-field
                                v-model="bettingData.stake"
                                :label="t('betDialog.stake')"
                                :rules="[rules.stake(Number(bettingData.stake)), minStakeWarning()]"
                                class="font14 bet bold"
                                density="compact"
                                :hide-details="true"
                                min="0"
                                step="1"
                                type="number"
                                variant="outlined"
                                @focus="focus()"
                                @input="change()"
                            >
                                <template v-slot:prepend-inner>
                                    <span :class="userStore.currency.id === Currency.Brl ? 'font12' : 'font14'" class="greyText font-weight-regular">
                                        {{ userStore.currency.symbol }}
                                    </span>
                                    <!--                                <v-icon color="grey" small class="pt-1" :icon="mdiCurrencyBrl" />-->
                                </template>
                            </v-text-field>
                        </v-col>
                        <v-col class="pt-2 pl-0">
                            <v-select
                                v-model="bettingData.timeout"
                                :items="getTimeoutDictionary"
                                :label="t('betDialog.timeout')"
                                class="font14 bet"
                                :class="[bettingData.timeout !== null ? 'bold' : '']"
                                density="compact"
                                :hide-details="true"
                                clearable
                                item-title="name"
                                item-value="id"
                                :rules="[rules.timeout()]"
                                variant="outlined"
                                @focus="focus()"
                                @input="change()"
                            ></v-select>
                        </v-col>
                    </v-row>
                    <v-row v-if="!bettingData.isOrderPlaced" class="px-0 my-0 h-56">
                        <v-col class="pl-2 pt-1 pr-0 col-label" cols="4">
                            <v-checkbox v-model="bettingData.isTopUps" :ripple="false" color="tbGreen" :true-value="1" :false-value="0" density="compact" :hide-details="true">
                                <template v-slot:label>
                                    <span class="font14 lightGreyText lh-18 pt-5px">
                                        <span>{{ t('betDialog.topUps').split(' ')[0] }}</span>
                                        <br />
                                        <span>{{ t('betDialog.topUps').split(' ')[1] }}</span>
                                    </span>
                                </template>
                            </v-checkbox>
                        </v-col>
                        <v-col class="font14 pt-2 px-1 d-flex text-center flex-column align-start justify-center col-label" cols="4">
                            <span class="lightGreyText">{{ t('betDialog.available') }}</span>
                            <span v-if="bettingData.oddsMaxStake && bettingData.odds" class="">{{ userStore.currency.symbol }}{{ formatNumber(bettingData.availableMaxStake, 'moneyNoCents') }}</span>
                            <span v-else>&nbsp;</span>
                        </v-col>
                        <v-col class="font14 pt-2 px-0 d-flex text-center flex-column align-start justify-center col-label" cols="4">
                            <span class="lightGreyText">{{ t('betDialog.returns') }}</span>
                            <span v-if="bettingData.stake && bettingData.odds" class="bold">{{ userStore.currency.symbol }}{{ formatNumber(bettingData.returns, 'money') }}</span>
                            <span v-else>&nbsp;</span>
                        </v-col>
                    </v-row>
                    <v-row v-if="platform.isAggregator() && !bettingData.isOrderPlaced && userStore.settings.bet_order_is_max_prices_default" class="px-3 my-0 font13">
                        <v-col class="pa-0" cols="4">
                            <div class="odds-buttons mr-1 cursorPointer" @click="setOdds(bettingData.bestOdds)">
                                <div class="odds-header pt-2px">
                                    {{ t('betDialog.best').toUpperCase() }}
                                </div>
                                <div v-if="bettingData.bestOdds" class="bold h-17">@{{ bettingData.bestOdds }}</div>
                                <div v-if="bettingData.bestMaxStake" class="h-18">{{ userStore.currency.symbol }}{{ formatNumber(bettingData.bestMaxStake, 'moneyNoCents') }}</div>
                            </div>
                        </v-col>
                        <v-col class="pa-0" cols="4">
                            <div class="odds-buttons cursorPointer" @click="setOdds(String(bettingData.medianOdds))">
                                <div class="odds-header pt-2px">
                                    {{ t('betDialog.median').toUpperCase() }}
                                </div>
                                <div v-if="bettingData.medianOdds" class="bold h-17">@{{ bettingData.medianOdds }}</div>
                                <div v-if="bettingData.medianMaxStake" class="h-18">{{ userStore.currency.symbol }}{{ formatNumber(bettingData.medianMaxStake, 'moneyNoCents') }}</div>
                            </div>
                        </v-col>
                        <v-col class="pa-0" cols="4">
                            <div class="odds-buttons ml-1 cursorPointer" @click="setOdds(bettingData.totalOdds)">
                                <div class="odds-header pt-2px">
                                    {{ t('betDialog.total').toUpperCase() }}
                                </div>
                                <div v-if="bettingData.totalOdds" class="bold h-17">@{{ bettingData.totalOdds }}</div>
                                <div v-if="bettingData.totalMaxStake" class="h-18">{{ userStore.currency.symbol }}{{ formatNumber(bettingData.totalMaxStake, 'moneyNoCents') }}</div>
                            </div>
                        </v-col>
                    </v-row>
                    <v-row v-if="bettingData.isOrderPlaced" class="ma-0">
                        <BetDialogBetInfo v-if="bettingData.isOrderPlaced" :id="id"></BetDialogBetInfo>
                    </v-row>
                    <v-row v-if="platform.isAggregator()" class="font13 pl-2 pr-3 pb-4 h-158">
                        <div v-for="(site, index) in siteOrdered" :key="index" :class="[index === 0 ? 'pl-1' : 'pl-1']" :style="stakeButtonStyle">
                            <div :class="[bettingData.isLowSiteMinStake(site) ? '' : 'bookmaker' + site, 'stake-button-header mb-1']">
                                <v-row :class="[bettingData.isLowSiteMinStake(site) ? '' : 'bookmaker' + site, 'my-auto pa-0 ma-0']">
                                    <v-col class="pr-0 py-0 my-auto" :class="display.width.value < 400 ? 'pl-2px' : 'pl-4px'">
                                        <label
                                            :for="'bookmaker' + site + '_popup' + bettingData.id"
                                            :data-tooltip="`${bookmakerDictionary.nameById(site)}\n${t('bot.minimumStake')}: ${userStore.currency.symbol}${bettingData.siteMinStake(site)}`"
                                        >
                                            {{ BookmakerCode[site] }}
                                        </label>
                                    </v-col>

                                    <v-col class="pa-0 ma-0 pr-1 w-22px">
                                        <v-checkbox
                                            v-model="sites"
                                            :id="'bookmaker' + site + '_popup' + bettingData.id"
                                            :class="[bettingData.isLowSiteMinStake(site) ? 'inactive' : 'bookmaker' + site, 'ma-0 pa-0 stake-button-checkbox']"
                                            :disabled="bettingData.isOrderPlaced"
                                            :ripple="false"
                                            :rules="[sitesValidation(sites)]"
                                            :value="site"
                                            density="compact"
                                            false-value="false"
                                            :hide-details="true"
                                            hide-spin-buttons
                                            required
                                            @change="change()"
                                        ></v-checkbox>
                                    </v-col>
                                </v-row>
                            </div>
                            <div
                                :class="[
                                    bettingData.isLowSiteMinStake(site) ? 'low-min-stake' : 'bookmaker' + site,
                                    bettingData.siteOdds[site] && bettingData.orderPlaced === null ? 'cursorPointer' : '',
                                ]"
                                class="stake-button"
                                @click="setOdds(bettingData.siteOdds[site])"
                            >
                                <div
                                    v-if="bettingData.siteOdds[site]"
                                    :class="[
                                        bettingData.siteOdds[site] == formatOdds(bettingData.price!) ? 'bold' : '',
                                        bettingData.getHighlightClass(site, bettingData.siteOdds[site]),
                                        theme.name.value === 'dark' ? 'whiteText' : 'text-black',
                                    ]"
                                    class="price"
                                >
                                    {{ normalizePrice(bettingData.siteOdds[site]) }}
                                </div>
                                <div class="h-16" v-else>
                                    <br />
                                    <br />
                                </div>
                                <div v-if="bettingData.siteOdds[site] && bettingData.oddsMaxStake && bettingData.oddsMaxStake[site]" class="max-stake text-center font10 font-weight-regular">
                                    {{ userStore.currency.symbol }}{{ formatNumber(bettingData.oddsMaxStake[site], 'moneyNoCents') }}
                                </div>
                                <div
                                    v-else-if="userStore.type === UserType.Player && bettingData.siteOdds[site]"
                                    :class="display.width.value > 385 && siteOrdered.length < 8 ? 'font9' : 'font8'"
                                    class="max-stake text-center redText font-weight-regular"
                                >
                                    {{ t('betDialog.requesting') }}
                                </div>
                                <div v-else class="max-stake">&nbsp;</div>
                            </div>
                            <div v-if="bettingData.siteOdds[site] && bettingData.extraOdds && bettingData.extraOdds[site]">
                                <div
                                    v-for="([extraOdds, maxStake], idx) in Object.entries(bettingData.extraOdds[site])"
                                    :class="[
                                        bettingData.isLowSiteMinStake(site) ? 'low-min-stake' : 'bookmaker' + site,
                                        bettingData.siteOdds[site] && bettingData.orderPlaced === null ? 'cursorPointer' : '',
                                    ]"
                                    class="extra-stake-button mt-1"
                                    @click="setOdds(String(extraOdds))"
                                    :key="index"
                                >
                                    <div v-if="bettingData.siteOdds[site]" v-memo="[idx, site, extraOdds]" :class="[bettingData.getHighlightClass(site, String(extraOdds), idx)]" class="price">
                                        <div class="h-16">{{ normalizePrice(String(extraOdds)) }}</div>
                                        <div class="max-stake font10 font-weight-regular">{{ userStore.currency.symbol }}{{ formatNumber(maxStake, 'moneyNoCents') }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </v-row>

                    <!--                    <v-row class="font13 pl-4 my-0 validation-error">-->
                    <!--                        <span>{{ errorMessage }}</span>-->
                    <!--                    </v-row>-->

                    <v-overlay
                        :model-value="userStore.type !== UserType.Player || (!bettingData.isActive && bettingData.orderPlaced === null)"
                        absolute
                        class="align-center justify-center mb-3"
                        contained
                        opacity="0.1"
                        persistent
                        scrim="red"
                    >
                        <v-chip color="red" label variant="flat">
                            <v-icon :icon="mdiAlertCircleOutline" />
                            <span v-if="userStore.type !== UserType.Player" class="white--text font-weight-bold font16">
                                {{ t('betDialog.notAuthorized') }}
                            </span>
                            <span v-else-if="!bettingData.isActive" class="white--text font-weight-bold font16">{{ t('betDialog.notAvailable') }}</span>
                        </v-chip>
                    </v-overlay>
                </v-card>
            </v-form>
            <v-divider></v-divider>
            <v-card-actions :class="display.xs.value ? '' : 'pb-0'" class="px-0 min-h-44">
                <span v-if="errorMessage" class="font11 pl-0 d-flex flex-column align-center justify-center validation-error" v-html="errorMessage"></span>
                <span v-else-if="warningMessage" class="font10 pl-0 d-flex flex-column align-center justify-center validation-warning">
                    {{ warningMessage }}
                </span>
                <v-spacer></v-spacer>
                <v-btn v-if="!bettingData.isOrderPlaced" density="default" color="grey" tag="a" variant="text" @click="cancel()">
                    {{ t('action.cancel') }}
                </v-btn>
                <v-btn
                    v-if="!bettingData.isOrderPlaced"
                    density="default"
                    :color="disabled ? 'grey' : 'tbGreen'"
                    :disabled="!bettingData.isActive || loading || disabled"
                    :loading="loading"
                    elevation="0"
                    class="text-white"
                    tag="a"
                    variant="flat"
                    @click="confirmAction()"
                >
                    {{ t('action.order') }}
                </v-btn>
                <v-btn
                    v-if="bettingData.isOrderPlaced"
                    density="default"
                    :disabled="!bettingData.isActive"
                    class="new-order-btn btn-green"
                    :color="theme.name.value === 'dark' ? 'tbGreen' : '#000000'"
                    elevation="0"
                    tag="a"
                    @click="newOrder()"
                >
                    {{ t('action.newOrder') }}
                </v-btn>
                <v-btn v-if="bettingData.isOrderPlaced" density="default" color="tbGreen" elevation="0" tag="a" variant="outlined" @click="cancel()">
                    {{ t('action.closeWindow') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
<style scoped src="./style.css"></style>
